import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { TooltipProvider } from '../ui/tooltip'
import Sidebar from './Sidebar'
import TopBar from './TopBar'
import { useAuth } from '../../context/AuthContext'
import { MARKETPLACE_ENABLED } from '../../config/featureFlags'
import { getDistributorConfig } from '../../services/distributorService'

interface AppLayoutProps {
  children: React.ReactNode
}

export default function AppLayout({ children }: AppLayoutProps) {
  const { partner } = useAuth()

  // Sidebar collapse state with localStorage persistence
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem('aitryx-sidebar-collapsed')
      return saved ? JSON.parse(saved) : false
    } catch {
      return false
    }
  })

  useEffect(() => {
    localStorage.setItem(
      'aitryx-sidebar-collapsed',
      JSON.stringify(sidebarCollapsed)
    )
  }, [sidebarCollapsed])

  const toggleSidebar = () => setSidebarCollapsed((prev: boolean) => !prev)

  // Global distributor health warning (migrated from old Layout)
  const [dickerDataWarning, setDickerDataWarning] = useState<{
    message: string
    detail?: string | null
  } | null>(null)

  const checkDistributorHealth = React.useCallback(async () => {
    try {
      if (!MARKETPLACE_ENABLED || !partner?.partnerId) return
      const cfg = await getDistributorConfig()
      if (cfg.apiToken && (cfg.lastSyncStatus || '').toLowerCase() === 'error') {
        setDickerDataWarning({
          message:
            'Dicker Data connection needs attention. Pricing/stock may be stale.',
          detail: cfg.lastSyncError,
        })
      } else {
        setDickerDataWarning(null)
      }
    } catch {
      // Silent fail
    }
  }, [partner?.partnerId])

  useEffect(() => {
    checkDistributorHealth()
    const onRefresh = () => checkDistributorHealth()
    const onFocus = () => checkDistributorHealth()
    const onVisibility = () => {
      if (document.visibilityState === 'visible') checkDistributorHealth()
    }

    window.addEventListener(
      'aitryx:distributor-health-refresh',
      onRefresh as EventListener
    )
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisibility)

    const id = window.setInterval(checkDistributorHealth, 5 * 60 * 1000)

    return () => {
      window.removeEventListener(
        'aitryx:distributor-health-refresh',
        onRefresh as EventListener
      )
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisibility)
      window.clearInterval(id)
    }
  }, [checkDistributorHealth])

  return (
    <TooltipProvider>
      <div className="flex h-screen overflow-hidden bg-slate-50">
        {/* Sidebar (desktop only - mobile handled by Sheet in TopBar) */}
        <Sidebar collapsed={sidebarCollapsed} onToggle={toggleSidebar} />

        {/* Main content area */}
        <div className="flex flex-col flex-1 overflow-hidden min-w-0">
          {/* TopBar includes environment badge, dev mode banner, header, and mobile menu */}
          <TopBar
            onSidebarToggle={toggleSidebar}
            sidebarCollapsed={sidebarCollapsed}
          />

          {/* Global distributor health warning */}
          {dickerDataWarning && (
            <div className="bg-rose-50 border-b border-rose-200 px-4 py-2 text-sm text-rose-900 shrink-0">
              <div className="max-w-[1800px] mx-auto px-2 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-medium">{dickerDataWarning.message}</div>
                  {dickerDataWarning.detail && (
                    <div className="text-xs text-rose-800 mt-0.5 truncate">
                      {dickerDataWarning.detail}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Link
                    to="/settings?tab=integrations"
                    className="text-xs font-semibold text-rose-700 hover:text-rose-900 underline"
                  >
                    Update token
                  </Link>
                  <button
                    type="button"
                    onClick={() => setDickerDataWarning(null)}
                    className="text-rose-600 hover:text-rose-900 text-lg leading-none"
                    aria-label="Dismiss Dicker Data warning"
                    title="Dismiss"
                  >
                    &times;
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Page content */}
          <main className="flex-1 overflow-auto">
            {children}
          </main>
        </div>
      </div>
    </TooltipProvider>
  )
}
